/**
 * Atmosphere Material
 * TSL-based material for atmospheric glow using fresnel effect
 */

import '../../../utils/webgpuPolyfill.js'
import * as THREE from 'three'
import materialConfig from '../config/materialConfig.json'

/**
 * Create atmosphere material with fresnel-based glow
 * @param {boolean} isWebGPU - Whether WebGPU renderer is being used
 * @param {Object} config - Optional config override for atmosphere parameters
 * @returns {Promise<MeshBasicNodeMaterial|MeshBasicMaterial>}
 */
export async function createAtmosphereMaterial(isWebGPU = true, config = null) {
  // Use provided config or fall back to materialConfig
  const atmosphereConfig = config || materialConfig
  // If not WebGPU, use simple WebGL fallback
  if (!isWebGPU) {
    const material = new THREE.MeshBasicMaterial({
      color: 0x4db2ff,
      transparent: true,
      opacity: 0.15,
      side: THREE.BackSide,
      depthWrite: false,
    })
    material.envMapIntensity = 0
    return material
  }

  // Try to load WebGPU modules dynamically
  let webgpuModules = null

  try {
    // Polyfill GPUShaderStage if not available (iOS Safari WebGPU)
    if (typeof GPUShaderStage === 'undefined' && typeof navigator !== 'undefined' && navigator.gpu) {
      window.GPUShaderStage = {
        VERTEX: 0x1,
        FRAGMENT: 0x2,
        COMPUTE: 0x4
      }
    }

    const [tsl, webgpu] = await Promise.all([
      import('three/tsl'),
      import('three/webgpu')
    ])

    webgpuModules = {
      normalWorld: tsl.normalWorld,
      positionWorld: tsl.positionWorld,
      cameraPosition: tsl.cameraPosition,
      normalize: tsl.normalize,
      dot: tsl.dot,
      abs: tsl.abs,
      pow: tsl.pow,
      max: tsl.max,
      smoothstep: tsl.smoothstep,
      mix: tsl.mix,
      vec3: tsl.vec3,
      vec4: tsl.vec4,
      tslFloat: tsl.float,
      mul: tsl.mul,
      sub: tsl.sub,
      clamp: tsl.clamp,
      uniform: tsl.uniform,
      MeshBasicNodeMaterial: webgpu.MeshBasicNodeMaterial,
    }
  } catch (e) {
    // WebGPU modules not available, will use WebGL fallback
  }

  // WebGL fallback
  if (!webgpuModules) {
    const material = new THREE.MeshBasicMaterial({
      color: 0x4db2ff,
      transparent: true,
      opacity: 0.15,
      side: THREE.BackSide,
      depthWrite: false,
    })
    material.envMapIntensity = 0
    return material
  }

  const {
    normalWorld, positionWorld, cameraPosition, normalize, dot, abs, pow, max,
    smoothstep, mix, vec3, vec4, tslFloat, mul, sub, clamp, uniform,
    MeshBasicNodeMaterial
  } = webgpuModules

  const material = new MeshBasicNodeMaterial({
    side: THREE.BackSide,
    transparent: true,
    depthWrite: false,
  })

  // Atmosphere colors from config
  const atmosphereDayColor = vec3(
    atmosphereConfig.atmosphereDayColor[0],
    atmosphereConfig.atmosphereDayColor[1],
    atmosphereConfig.atmosphereDayColor[2]
  )
  const atmosphereTwilightColor = vec3(
    atmosphereConfig.atmosphereTwilightColor[0],
    atmosphereConfig.atmosphereTwilightColor[1],
    atmosphereConfig.atmosphereTwilightColor[2]
  )

  // Calculate fresnel effect (edge glow)
  const viewDirection = positionWorld.sub(cameraPosition).normalize()
  const fresnel = dot(viewDirection, normalWorld).abs().oneMinus()

  // Create uniform for dynamic sun position that can be updated
  const lightPositionUniform = uniform(new THREE.Vector3(-5.0, 5.0, 25.0))
  const sunOrientation = dot(normalWorld, normalize(lightPositionUniform)).negate()

  // Blend between twilight and day colors based on sun orientation
  // Negated so positive values are night side, creating proper color transition
  const atmosphereColor = mix(
    atmosphereTwilightColor,
    atmosphereDayColor,
    smoothstep(tslFloat(-0.25), tslFloat(0.75), sunOrientation)
  )

  // Calculate alpha using fresnel and sun orientation
  // Remap fresnel from [config.start, 1] to [1, 0] for edge glow
  const fresnelStart = tslFloat(atmosphereConfig.atmosphereFresnelStart)
  const fresnelEnd = tslFloat(1.0)
  const fresnelRange = fresnelEnd.sub(fresnelStart)
  
  // Add small epsilon to avoid division by very small numbers
  const epsilon = tslFloat(0.0001)
  const safeDivisor = max(fresnelRange, epsilon)
  
  const fresnelNormalized = fresnel.sub(fresnelStart).div(safeDivisor).clamp(0, 1)
  const fresnelRemapped = tslFloat(1.0).sub(fresnelNormalized)
  
  // Apply power for sharper falloff with additional smoothing
  const fresnelPowered = pow(fresnelRemapped.clamp(0, 1), tslFloat(atmosphereConfig.atmosphereFresnelPower))
  
  // Modulate by sun orientation (brighter on lit side)
  const atmosphereDayStrength = smoothstep(tslFloat(-0.5), tslFloat(1.0), sunOrientation)
  const alphaBase = mul(fresnelPowered, atmosphereDayStrength).clamp(0, 1)
  const alpha = mul(alphaBase, tslFloat(atmosphereConfig.atmosphereIntensity)).clamp(0, 1)

  // Opacity fade uniform for camera-distance fade (default fully opaque)
  const opacityFadeUniform = uniform(1.0)

  // Set final output — multiply alpha by fade uniform
  material.colorNode = atmosphereColor
  material.opacityNode = alpha.mul(opacityFadeUniform)

  // Store reference to the uniform node itself for updating
  material.userData.lightPositionUniformNode = lightPositionUniform
  material.userData.opacityFadeUniform = opacityFadeUniform

  return material
}

/**
 * Create inner atmosphere material — rendered FrontSide on top of Earth's surface.
 * Provides a subtle fresnel-based atmospheric haze visible when looking at the Earth face-on.
 * @param {boolean} isWebGPU
 * @param {Object} config
 * @returns {Promise<MeshBasicNodeMaterial|MeshBasicMaterial>}
 */
export async function createInnerAtmosphereMaterial(isWebGPU = true, config = null) {
  const atmosphereConfig = config || materialConfig

  if (!isWebGPU) {
    const material = new THREE.MeshBasicMaterial({
      color: 0x4db2ff,
      transparent: true,
      opacity: 0.08,
      side: THREE.FrontSide,
      depthWrite: false,
    })
    material.envMapIntensity = 0
    return material
  }

  let webgpuModules = null
  try {
    if (typeof GPUShaderStage === 'undefined' && typeof navigator !== 'undefined' && navigator.gpu) {
      window.GPUShaderStage = { VERTEX: 0x1, FRAGMENT: 0x2, COMPUTE: 0x4 }
    }
    const [tsl, webgpu] = await Promise.all([
      import('three/tsl'),
      import('three/webgpu')
    ])
    webgpuModules = {
      normalWorld: tsl.normalWorld,
      positionWorld: tsl.positionWorld,
      cameraPosition: tsl.cameraPosition,
      normalize: tsl.normalize,
      dot: tsl.dot,
      pow: tsl.pow,
      smoothstep: tsl.smoothstep,
      mix: tsl.mix,
      vec3: tsl.vec3,
      tslFloat: tsl.float,
      mul: tsl.mul,
      uniform: tsl.uniform,
      MeshBasicNodeMaterial: webgpu.MeshBasicNodeMaterial,
    }
  } catch (e) {
    // fallback
  }

  if (!webgpuModules) {
    const material = new THREE.MeshBasicMaterial({
      color: 0x4db2ff,
      transparent: true,
      opacity: 0.08,
      side: THREE.FrontSide,
      depthWrite: false,
    })
    material.envMapIntensity = 0
    return material
  }

  const {
    normalWorld, positionWorld, cameraPosition, normalize, dot, pow,
    smoothstep, mix, vec3, tslFloat, mul, uniform,
    MeshBasicNodeMaterial
  } = webgpuModules

  const material = new MeshBasicNodeMaterial({
    side: THREE.FrontSide,
    transparent: true,
    depthWrite: false,
  })

  // Same day/twilight color blend as outer atmosphere
  const atmosphereDayColor = vec3(
    atmosphereConfig.atmosphereDayColor[0],
    atmosphereConfig.atmosphereDayColor[1],
    atmosphereConfig.atmosphereDayColor[2]
  )
  const atmosphereTwilightColor = vec3(
    atmosphereConfig.atmosphereTwilightColor[0],
    atmosphereConfig.atmosphereTwilightColor[1],
    atmosphereConfig.atmosphereTwilightColor[2]
  )

  // Fresnel for FrontSide: bright at edges (grazing angle), transparent at center
  const viewDirection = positionWorld.sub(cameraPosition).normalize()
  const fresnel = dot(viewDirection, normalWorld).abs().oneMinus()

  // Sun orientation for color blending and day/night modulation
  // FrontSide normals point outward — no negate needed (unlike BackSide outer atmosphere)
  const lightPositionUniform = uniform(new THREE.Vector3(-5.0, 5.0, 25.0))
  const sunOrientation = dot(normalWorld, normalize(lightPositionUniform))

  const atmosphereColor = mix(
    atmosphereTwilightColor,
    atmosphereDayColor,
    smoothstep(tslFloat(-0.25), tslFloat(0.75), sunOrientation)
  )

  // Inner atmosphere: fresnel raised to power for edge-concentrated glow on the surface
  const fresnelPower = tslFloat(atmosphereConfig.innerAtmosphereFresnelPower || 2.8)
  const fresnelGlow = pow(fresnel, fresnelPower)

  // Modulate by sun orientation — only visible on lit side, sharp cutoff at terminator
  const dayStrength = smoothstep(tslFloat(-0.1), tslFloat(0.5), sunOrientation)
  const intensity = tslFloat(atmosphereConfig.innerAtmosphereIntensity || 0.35)
  const alpha = mul(mul(fresnelGlow, dayStrength), intensity).clamp(0, 1)

  // Opacity fade uniform for camera-distance fade
  const opacityFadeUniform = uniform(1.0)

  material.colorNode = atmosphereColor
  material.opacityNode = alpha.mul(opacityFadeUniform)

  material.userData.lightPositionUniformNode = lightPositionUniform
  material.userData.opacityFadeUniform = opacityFadeUniform

  return material
}
